// 消息发送与流式响应处理 Hook
import { useCallback, useRef } from "react";
import { Modal } from "antd";
import axios from "axios";
import {
    pickAnswerText,
    getFlowOutputThoughtText,
    getFinalThoughtText,
    applyStreamMessageUpdate,
    mergeStreamText,
    STREAM_RENDER_TRIGGER_MODES,
    processJSONBuffer,
} from "../utils/streamUtils";
import { currentStreamMode, STREAM_MODE } from "../pages/RecipeCustomization/recipeAi/streamConfig";
import { useScrollToBottom } from "./useScrollToBottom";
import {
    NONE_PROFILE_ID,
    buildPersonInfoFromProfile,
} from "../utils/profileUtils";

const STREAM_RENDER_TRIGGER_MODE = STREAM_RENDER_TRIGGER_MODES.END_NODE;
const CHAT_RESPONSE_TIMEOUT = 90000;

/** 模块级引用：外部注册的会员升级回调 */
let _quotaUpgradeCallback = null;

/**
 * 暴露给 UI 层调用：触发会员升级流程
 */
export const handleQuotaUpgrade = () => {
    _quotaUpgradeCallback?.();
};

/**
 * 从 Response 中读取流式数据并逐包处理
 */
const processStreamResponse = async (response, targetSetter, timerRef, processData) => {
    if (!response.ok || !response.body) throw new Error("响应异常");
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (value) {
                buffer += decoder.decode(value, { stream: !done });
                buffer = processData(buffer, done, targetSetter, timerRef);
            }
            if (done) break;
        }
    } catch (e) {
        // 重新抛出错误，确保 AbortError 等能被上层 catch 正确捕获
        throw e;
    } finally {
        // releaseLock 在 reader 已被 abort 时会抛异常，必须安全处理
        try {
            reader.releaseLock();
        } catch {
            // reader 因 abort 已自动释放锁，忽略
        }
    }
};

/**
 * 对话发送 + 流式数据处理 Hook（纯工具，不感知页面）
 */
export const useSendMessage = ({
    setMessages, // 更新消息列表的 state setter
    setIsLoading, // 更新加载状态（控制 loading 动画/按钮禁用）
    messages, // 当前消息列表 state
    isLoading, // 当前是否正在加载中（请求进行中）
    abortRef, // AbortController 的 ref，用于取消正在进行的流式请求
    sessionIdRef, // 当前对话会话 ID 的 ref（用于多轮对话上下文关联）
    thinkingTimerRef, // 前端模拟"思考中"的定时器 ref（显示 AI 正在思考）
    agentId, // 页面标识常量：0=主页面, 1=智能体页面, 2=营养师页面
    userId, // 当前用户 ID，用于请求鉴权和消息归属
    containerRef, // 消息滚动容器的 DOM ref
    resolveProfile, // 解析档案信息的回调，将 profileCode + personInfo 转为可用数据
    profileNickRef, // 当前档案昵称的 ref（如"孩子"、"本人"）
    profileIdRef, // 当前档案 ID 的 ref
    onProfileUpdateRequest, // 触发档案更新流程的回调（跳转档案编辑页）
    externalUrlRef, // 外部指定的 API 路径 ref（如从食谱页跳转时传入的 url）
    externalPayloadRef, // 外部预设的请求体 ref（如从食谱页跳转时传入的 chatPayload）
    streamingTTSRef, // 全局实时语音播报控制器 ref
    onQuotaUpgrade, // code=2012 时触发会员升级的回调
}) => {
    // 同步到模块级引用，供外部和 Message 组件调用
    _quotaUpgradeCallback = onQuotaUpgrade || null;
    const isNutri = agentId === 2;

    // 记录已触发过 @update 弹窗的请求 ID（Hook 内部管理）
    const triggeredUpdateRequests = useRef(new Set());

    // 记录当前流式消息的累计完整文本（合并规则与 applyStreamMessageUpdate 渲染的 rawText 保持一致），
    // 供 TTS 在重新打开朗读开关时从头朗读完整内容
    const ttsFullTextRef = useRef("");

    /**
     * 将流式完整累计文本投喂给全局 TTS
     *
     * 打开开关时：
     * - 已有文本：立即从头朗读最新完整内容
     * - 思考中：首个文本包到达时从头朗读
     * 播到一半关闭后再开启（流式未结束）：从头朗读最新完整内容，后续增量无缝衔接
     * @param {string} fullText 当前累计完整文本
     */
    const feedStreamingTTS = useCallback((fullText) => {
        streamingTTSRef?.current?.feedFullText(fullText);
    }, [streamingTTSRef]);

    // 设置今日提醒
    const setTodayRemind = async () => {
        const profileId = profileIdRef?.current;
        if (profileId) {
            await axios.post(
                `${window.PUBLIC_URL}/medagent/robot/setTodayRemind`,
                { id: profileId },
            );
        }
    };

    // --- 滚动控制 ---
    const {
        scrollToBottom,
        showScrollToBottom,
        checkScrollPosition,
        buttonBottom,
    } = useScrollToBottom({ containerRef, isLoading, messages });

    // ========================
    //  错误处理
    // ========================
    const handleRequestError = (error, messageId, targetSetter, isTimeout = false) => {
        targetSetter((prev) =>
            prev.map((msg) => {
                if (msg.id !== messageId) return msg;
                const finalizedSteps = msg.thoughtSteps
                    ? msg.thoughtSteps.map((s) => ({ ...s, status: 2 }))
                    : null;

                if (isTimeout) {
                    return {
                        ...msg,
                        text: "抱歉，当前咨询分析耗时较长，请尝试减少上传图片数量或稍后再次提问～",
                        ending: true,
                        loading: false,
                    };
                }

                if (error.name === "AbortError")
                    return {
                        ...msg,
                        text: "请求已取消",
                        ending: true,
                        loading: false,
                        cancelled: true,
                    };

                return {
                    ...msg,
                    loading: false,
                    ending: true,
                    thoughtSteps: finalizedSteps,
                    text: `抱歉，服务暂时出现异常（${error.message || "未知错误"}），请稍后重试。`,
                };
            }),
        );
    };

    // ========================
    //  流式 JSON 数据包处理
    // ========================
    const handlePacket = useCallback(
        (packet, targetSetter, timerRef) => {
            const record = packet.output;
            const sessionId = record?.sessionId || "";
            const requestId = packet?.requestId || "";
            // 每个数据包可能携带独立的 planCode / recordDate / 结构化卡片字段，绑定到对应消息上
            const packetPlanCode = packet?.planCode 
            const packetRecordDate = packet?.recordDate
            // 结构化卡片字段（dietRecordData/sportRecordData）：后端随包返回对象（AI 不再拼接卡片 HTML，
            // 由前端对应 Card 组件渲染）。数据优先取 record，兜底 packet。
            const packetDietRecordData = record?.dietRecordData || packet?.dietRecordData;
            const packetSportRecordData = record?.sportRecordData || packet?.sportRecordData;
            const hasStructuredCardData =
                (packetDietRecordData && typeof packetDietRecordData === "object") ||
                (packetSportRecordData && typeof packetSportRecordData === "object");
            let endNodeData = null;

            // 同步 SessionId
            if (sessionId) {
                sessionIdRef.current = sessionId;
            }

            let hasRenderableThoughtText = false;
            let shouldFinishByThoughtNode = false;

            if (currentStreamMode === STREAM_MODE.MESSAGE_FORMAT_PLUS) {
                const workflowMessage = record?.workflowMessage;
                if (workflowMessage?.nodeType?.toLowerCase() === "end") {
                    endNodeData = pickAnswerText(workflowMessage?.message?.content);
                    hasRenderableThoughtText = !!endNodeData;
                    shouldFinishByThoughtNode = workflowMessage?.nodeIsCompleted === true;
                }
            } else if (record?.thoughts?.length) {
                if (STREAM_RENDER_TRIGGER_MODE === STREAM_RENDER_TRIGGER_MODES.FLOW_OUTPUT) {
                    const flowOutputText = getFlowOutputThoughtText(record.thoughts);
                    const finalThoughtText = getFinalThoughtText(record.thoughts);
                    if (flowOutputText) {
                        endNodeData = flowOutputText;
                        hasRenderableThoughtText = true;
                    }
                    if (finalThoughtText) {
                        endNodeData = finalThoughtText;
                        hasRenderableThoughtText = true;
                        shouldFinishByThoughtNode = true;
                    }
                } else {
                    const finalThoughtText = getFinalThoughtText(record.thoughts);
                    if (finalThoughtText) {
                        endNodeData = finalThoughtText;
                        hasRenderableThoughtText = true;
                        shouldFinishByThoughtNode = true;
                    }
                }
            }

            // 业务错误码（如 2012 对话次数上限）也视为流结束
            const hasBusinessError = !record && packet?.code && Number(packet.code) !== 200;
            const hasFinishReason =
                record?.finishReason === "stop" || hasBusinessError;
            // 记录次数上限标记，供 UI 展示「去开通」按钮
            const quotaExceeded = packet?.code === 2012;
            let displayText = "";

            if (currentStreamMode !== STREAM_MODE.MESSAGE_FORMAT_PLUS) {
                const deltaText = pickAnswerText(
                    record?.text || record?.answer || record?.content || record?.result,
                );
                displayText = deltaText || (!record ? pickAnswerText(
                    packet?.output || packet?.data || packet?.answer || packet?.content
                    || packet?.result || (packet?.code && Number(packet.code) !== 200
                        ? packet?.msg || packet?.message : ""),
                ) : "");
            } else {
                displayText = record?.text;
                if (!displayText && hasBusinessError) {
                    displayText = pickAnswerText(packet?.msg || packet?.message);
                }
            }

            // @update 弹窗触发
            if (
                displayText &&
                /@update/i.test(displayText) &&
                requestId &&
                !triggeredUpdateRequests.current.has(requestId)
            ) {
                triggeredUpdateRequests.current.add(requestId);
                const personName = profileNickRef?.current || "孩子";
                Modal.confirm({
                    title: "提示",
                    content: `今日${personName}的身高体重还没更新哦`,
                    centered: true,
                    okText: "去更新",
                    cancelText: "取消",
                    onOk: async () => {
                        try {
                            await setTodayRemind();
                            onProfileUpdateRequest?.();
                        } catch (e) {
                            console.error("更新状态失败:", e);
                        }
                    },
                    onCancel: async () => {
                        try {
                            await setTodayRemind();
                        } catch (e) {
                            console.error("更新状态失败:", e);
                        }
                    },
                });
            }

            if (hasRenderableThoughtText || hasFinishReason || displayText || packetPlanCode || packetRecordDate || hasStructuredCardData) {
                if ((hasRenderableThoughtText || hasFinishReason) && timerRef?.current) {
                    console.log(">>> [流量接管] 正文已到达，清理前端模拟计时器");
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }

                // 计算与 UI 渲染完全一致的累计完整文本（合并优先级与 applyStreamMessageUpdate 保持一致）
                let cumulativeText = ttsFullTextRef.current || "";
                if (hasFinishReason && record?.text) {
                    cumulativeText = record.text;
                } else if (hasRenderableThoughtText && endNodeData) {
                    cumulativeText = mergeStreamText(cumulativeText, endNodeData);
                } else if (displayText) {
                    cumulativeText = displayText;
                }
                ttsFullTextRef.current = cumulativeText;

                // 全局实时语音播报：投喂完整累计文本
                // 打开开关时：已有文本立即从头朗读，思考中则首个文本包到达时从头朗读；
                // 播到一半关闭再开启（流式未结束）：从头朗读最新完整内容，后续增量无缝衔接
                if (cumulativeText) {
                    feedStreamingTTS(cumulativeText);
                }

                // 结束节点或 finishReason 到达时，通知 TTS  flush 剩余缓冲
                if (hasFinishReason || shouldFinishByThoughtNode) {
                    streamingTTSRef?.current?.finish();
                }

                targetSetter((prevMessages) => {
                    return applyStreamMessageUpdate(prevMessages, {
                        shouldFinishByThoughtNode,
                        hasFinishReason,
                        hasRenderableThoughtText,
                        record,
                        endNodeData,
                        displayText,
                        sessionId,
                        requestId,
                        quotaExceeded,
                        planCode: packetPlanCode,
                        recordDate: packetRecordDate,
                        dietRecordData: packetDietRecordData,
                        sportRecordData: packetSportRecordData,
                    });
                });
            }
        },
        [
            sessionIdRef,
            profileNickRef,
            profileIdRef,
            onProfileUpdateRequest,
            setTodayRemind,
            streamingTTSRef,
            feedStreamingTTS,
        ],
    );

    const processData = useCallback(
        (buffer, isFinal, targetSetter, timerRef) => {
            return processJSONBuffer(buffer, isFinal, (packet) =>
                handlePacket(packet, targetSetter, timerRef),
            );
        },
        [handlePacket],
    );

    /**
     * 按档案 code 反查该档案的【最新】完整数据。
     * 以 profileCode 为准请求后端最新档案列表再匹配（不信任内存 currentProfile / 历史卡片快照），
     * 确保发送请求中 profileCode 与 personInfo 同源且不过期。
     * @param {string} requestedCode 档案 code（如饮食记录卡片所属档案）
     * @returns {Promise<object|null>} 命中的最新档案对象；未命中 / 请求失败返回 null
     */
    const fetchLatestProfileByCode = async (requestedCode) => {
        if (!requestedCode || !userId) return null;
        try {
            const { data: res } = await axios.post(
                `${window.PUBLIC_URL}/medagent/robot/searchProfile`,
                { askUserCode: userId },
            );
            if (res?.code !== 200 || !Array.isArray(res.data)) return null;
            const matched = res.data.find(
                (p) =>
                    String(p?.profileCode || p?.id || p?.code || "") ===
                    String(requestedCode),
            );
            if (!matched || matched?.id === NONE_PROFILE_ID) return null;
            return matched;
        } catch (err) {
            console.warn("按档案 code 反查最新档案失败:", err);
            return null;
        }
    };

    // ========================
    //  主发送逻辑
    // ========================
    const handleSendMessage = async (_input, fileList, personInfo = null, options = {}) => {
        const uploadFileList = Array.isArray(fileList?.list) ? fileList : null;

        if (isLoading) return;

        const _inputValue = _input?.trim();
        const hasFiles = uploadFileList?.list?.length > 0;
        if (!_inputValue && !hasFiles) return;

        // 新消息开始时重置全局实时语音播报
        streamingTTSRef?.current?.reset();
        ttsFullTextRef.current = "";

        // 单次发送可显式指定档案（如饮食记录卡片“确认修改”按卡片所属档案 record.profile 发送）
        const requestedProfileCode = options?.profileCode;

        let finalProfileCode = "";
        let finalPersonInfo = personInfo;

        if (requestedProfileCode) {
            // 显式指定了档案 code（通常是跨档案场景）：以该 code 为准反查【最新】档案数据。
            // 不信任内存 currentProfile（当前可能选中另一条档案）与历史卡片快照（可能过期），
            // 也不回退成当前档案的 personInfo，避免档案 A 的身体数据发到档案 B 上。
            finalProfileCode = requestedProfileCode;
            const latestProfile = await fetchLatestProfileByCode(requestedProfileCode);
            if (latestProfile) {
                finalPersonInfo = buildPersonInfoFromProfile(latestProfile) || {};
            } else {
                // 反查不到该档案（已删除 / 未命中）：宁缺勿错，携带空 personInfo
                console.warn(
                    "未命中档案 code，本次发送 personInfo 置空:",
                    requestedProfileCode,
                );
                finalPersonInfo = {};
            }
        } else {
            // 默认：沿用既有委托，按“当前选中档案”解析
            const { profileCode: defaultCode = "", personInfo: defaultPersonInfo = null } =
                await resolveProfile?.(isNutri) || {};
            finalProfileCode = defaultCode;

            if (isNutri && !finalPersonInfo && defaultPersonInfo === null) {
                finalPersonInfo = {};
            }
            if (!finalPersonInfo) {
                finalPersonInfo = defaultPersonInfo;
            }
        }

        const userMessage = {
            id: Date.now(),
            text: _inputValue,
            sender: "user",
            file: uploadFileList || { type: "", list: [] },
            isHidden: !!personInfo,
        };
        const initialSteps = [
            { label: "对话数据整理", nodeNames: ["对话数据整理"], status: 1 },
            { label: "知识库查询", nodeNames: ["知识库查询"], status: 0 },
            { label: "指南共识查询", nodeNames: ["指南共识查询"], status: 0 },
        ];
//AI分身的逻辑,按照以前的逻辑
        if (hasFiles&&agentId==1) {
            initialSteps.push({ label: "报告解读", nodeNames: ["报告解读"], status: 0 });
        }

        const assistantMessageId = Date.now() + 1;
        const assistantMessage = {
            id: assistantMessageId,
            sender: "assistant",
            ending: false,
            loading: true,
            thoughtSteps: initialSteps,
        };

        setMessages((prev) => [...prev, userMessage, assistantMessage]);
        // 记录待点亮喇叭的消息 ID：仅当首个实际流式内容 chunk 到达时才点亮，
        // 避免消息占位（思考中）阶段就显示自动播报状态
        streamingTTSRef?.current?.armStreamingMessageId(assistantMessageId);
        setIsLoading(true);

        setTimeout(() => scrollToBottom(true), 200);

        let stepIdx = 0;
        const maxSteps = initialSteps.length;

        thinkingTimerRef.current = setInterval(() => {
            if (stepIdx >= maxSteps - 1) {
                clearInterval(thinkingTimerRef.current);
                thinkingTimerRef.current = null;
                return;
            }
            stepIdx++;
            setMessages((prev) =>
                prev.map((msg) => {
                    if (msg.id !== assistantMessageId) return msg;
                    const newSteps = [...msg.thoughtSteps];
                    if (newSteps[stepIdx - 1]) newSteps[stepIdx - 1].status = 2;
                    if (newSteps[stepIdx]) newSteps[stepIdx].status = 1;
                    return { ...msg, thoughtSteps: newSteps };
                }),
            );
        }, 1200);

        const controller = new AbortController();
        abortRef.current = controller;

        const timeoutId = setTimeout(() => {
            if (abortRef.current === controller) {
                controller.abort("timeout");
            }
        }, CHAT_RESPONSE_TIMEOUT);

        try {
            // 外部跳转传入的预设 payload（如食谱页跳转）
            const externalPayload = externalPayloadRef?.current || {};

            const chatPayload = {
                ...externalPayload,
                input: _inputValue,
                agentId: externalPayload.agentId ?? agentId,
                additionalInfoId:
                    uploadFileList?.list?.map((item) => item.fileName) || [],
                common: {
                    channel: "0",
                    from: "wt",
                    userId: userId,
                    sessionId:
                        sessionIdRef.current ||
                        messages?.at(-1)?.sessionId ||
                        "",
                    ...(externalPayload.common || {}),
                },
            };

            if (agentId === 2 && !chatPayload.businessType) {
                chatPayload.businessType = "dt";
            }
            if (finalProfileCode) {
                chatPayload.profileCode = finalProfileCode;
            }
            if (options?.cardOrChatFlag != null) {
                chatPayload.cardOrChatFlag = options.cardOrChatFlag;
            }
            if (options?.dietRecord != null) {
                chatPayload.dietRecord = options.dietRecord;
            }
            if (options?.sportRecord != null) {
                chatPayload.sportRecord = options.sportRecord;
            }
            if (finalPersonInfo) {
                chatPayload.personInfo = finalPersonInfo;
            }

            console.log("body", JSON.stringify(chatPayload));

            // 外部指定 URL 时走外部接口，否则走默认 chat 接口
            const apiUrl = externalUrlRef?.current
                ? `${window.PUBLIC_URL}${externalUrlRef.current}`
                : `${window.PUBLIC_URL}/medagent/robot/chat`;

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(chatPayload),
                signal: controller.signal,
            });

            await processStreamResponse(response, setMessages, thinkingTimerRef, processData);

            if (thinkingTimerRef.current) {
                clearInterval(thinkingTimerRef.current);
                thinkingTimerRef.current = null;
            }

            // 流正常结束，通知 TTS flush 剩余文本
            streamingTTSRef?.current?.finish();

            setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last && last.id === assistantMessageId && last.loading) {
                    return prev.map((m) =>
                        m.id === assistantMessageId
                            ? {
                                ...m,
                                text: "当前服务没有返回可展示的回答，请稍后再试，或切换具体人员信息后重新咨询。",
                                loading: false,
                                ending: true,
                                thoughtSteps: m.thoughtSteps?.map((s) => ({ ...s, status: 2 })),
                            }
                            : m,
                    );
                }
                return prev;
            });
        } catch (error) {
            const isAbort = error?.name === "AbortError";
            const isTimeout = isAbort && controller.signal.reason === "timeout";

            // 用户主动取消是预期行为，降级日志避免误导
            if (isAbort && !isTimeout) {
                console.log(">>> [对话请求] 用户主动取消，请求已中止");
            } else if (isTimeout) {
                console.warn(">>> [对话请求] 超时未响应:", error?.message);
            } else {
                console.group(">>> [对话请求异常]");
                console.error("Error:", error);
                console.error("Name:", error?.name);
                console.error("Message:", error?.message);
                console.error("Stack:", error?.stack);
                console.log("Context:", {
                    agentId,
                    assistantMessageId,
                    sessionId: sessionIdRef.current || "",
                });
                console.groupEnd();
            }

            if (thinkingTimerRef.current) {
                clearInterval(thinkingTimerRef.current);
                thinkingTimerRef.current = null;
            }
            // 异常/取消时停止播报
            streamingTTSRef?.current?.reset();
            ttsFullTextRef.current = "";
            handleRequestError(error, assistantMessageId, setMessages, isTimeout);
        } finally {
            clearTimeout(timeoutId);
            setIsLoading(false);
            if (abortRef.current === controller) abortRef.current = null;
            // 清除待点亮标记，避免残留影响下一次消息
            streamingTTSRef?.current?.armStreamingMessageId(null);
        }
    };

    return { handleSendMessage, scrollToBottom, showScrollToBottom, checkScrollPosition, buttonBottom };
};
